import React, { useEffect, useState } from 'react';
import { Download, Eye, CreditCardIcon, RefreshCw } from 'lucide-react';
import { transactionApi } from '../../../repositories/transaction';
import { handleError } from '../../../utils/errorHandler';
import Swal from 'sweetalert2';
import DataTable, { DataTableColumn } from '../../ui/DataTable';
import Pagination from '../../common/Pagination';
import SettingsPanel from '../../ui/SettingsPanel';
import Button from '../../ui/Button';
import { buttonVariants } from '../../ui/buttonVariants';
import { Skeleton } from '../../ui/skeleton';

interface Invoice {
  id: number;
  invoiceNumber: string;
  amount: number;
  currency: string;
  creditsPurchased: number;
  packageName: string;
  invoiceDate: string;
  status: string;
  pdfPath: string;
}

interface ApiResponse {
  success: boolean;
  data: Invoice[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalItems: number;
  };
}

function Billing() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchInvoices = async (pageNum: number = 1) => {
    setLoading(true);
    try {
      const response = await transactionApi.getUserInvoices({ page: pageNum, limit: 10 });
      const data: ApiResponse = response.data;
      
      if (data.success) {
        setInvoices(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.totalItems);
        setPage(data.pagination.page);
      } else {
        throw new Error('Failed to fetch invoices');
      }
    } catch (error) {
      handleError(error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleOpenInvoice = (invoice: Invoice) => {
    if (!invoice.pdfPath) {
      Swal.fire({
        icon: 'error',
        title: 'Invoice Unavailable',
        text: 'No invoice PDF is attached to this transaction.',
      });
      return;
    }
    /*
      Opened in a new tab rather than forced through a download attribute: the
      PDF is served from S3, and `download` is ignored cross-origin, so the
      browser saved whatever the bucket returned. When the object wasn't
      publicly readable that was S3's error XML written to an .pdf file, which
      is why the saved file failed to open while the same URL rendered fine
      when followed directly. This is the same way the Pitch Pack tab links to
      the very same `pdfPath`.
    */
    window.open(invoice.pdfPath, '_blank', 'noopener,noreferrer');
  };

  const handleViewReceipt = (invoiceId: number) => {
    // For now, we'll show a message since receipt viewing might be handled differently
    Swal.fire({
      icon: 'info',
      title: 'Receipt Viewing',
      text: 'Receipt viewing is available in your Stripe dashboard.',
    });
  };

  const handleRefresh = () => {
    fetchInvoices(page);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchInvoices(newPage);
    }
  };

  const rowActionClass = buttonVariants({ variant: "link", size: "inline" });

  const invoiceColumns: DataTableColumn<Invoice>[] = [
    { key: "date", header: "Date", cell: (i) => <span className="text-neutral-600">{formatDate(i.invoiceDate)}</span> },
    {
      key: "description",
      header: "Description",
      cell: (i) => (
        <span className="font-medium">
          {i.creditsPurchased} Pitch Credits - {i.packageName}
        </span>
      ),
    },
    { key: "amount", header: "Amount", cell: (i) => <span className="text-neutral-600">{formatCurrency(i.amount, i.currency)}</span> },
    {
      key: "invoice",
      header: "Invoice",
      cell: (i) => (
        <button onClick={() => handleOpenInvoice(i)} className={rowActionClass}>
          <Download size={16} className="mr-1" />
          Download
        </button>
      ),
    },
    {
      key: "receipt",
      header: "Receipt",
      // Was green — but this is a link to a document, not a success state.
      cell: (i) => (
        <button onClick={() => handleViewReceipt(i.id)} className={rowActionClass}>
          <Eye size={16} className="mr-1" />
          View in Stripe
        </button>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Billing History"
      description="Your past invoices and transaction records"
      divided
      actions={
        <Button
          variant="secondary"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw
            size={16}
            className={loading ? 'animate-spin' : ''}
          />
          Refresh
        </Button>
      }
    >

      {/* Header with icon */}
      <div className="flex items-center mb-4 sm:mb-6">
        <CreditCardIcon className="w-5 h-5 mr-2 text-primary-700 sm:w-6 sm:h-6" />
        <h3 className="text-sm font-semibold text-black sm:text-base">
          Transaction Records
        </h3>
      </div>

      {/*
        Billing Table. The empty state is DataTable's own, so an empty result no
        longer renders a bordered header with a second, separate message below it.

        Loading is scoped to the rows rather than the whole card: a refresh used
        to replace the heading and the Refresh button itself with placeholders,
        so the control the user had just clicked vanished under their cursor and
        the panel jumped.
      */}
      <DataTable
        columns={invoiceColumns}
        rows={invoices}
        getRowKey={(invoice: Invoice) => invoice.id}
        loading={loading}
        skeletonRows={5}
        emptyMessage={
          <span className="block">
            <CreditCardIcon className="w-12 h-12 mx-auto text-neutral-400" />
            <span className="block mt-2 text-sm font-medium text-black">No billing records found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Your transaction history will appear here once you make purchases.
            </span>
          </span>
        }
      />

      {/* Summary section. Held back while refreshing so the totals never show a
          figure that disagrees with the rows above them. */}
      {/* Placeholder band in the summary's place, so the page doesn't grow when it lands. */}
      {loading && <Skeleton className="h-[74px] mt-6 rounded-lg" />}
      {!loading && invoices.length > 0 && (
        <div className="p-4 mt-6 border border-primary-200 rounded-lg bg-primary-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-primary-800">
                Total Transactions
              </h4>
              <p className="text-2xl font-bold text-primary-900">
                {totalItems}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-primary-700">
                Total Amount: {formatCurrency(invoices.reduce((sum, invoice) => sum + invoice.amount, 0))}
              </p>
              <p className="text-xs text-primary-700">
                All transactions processed securely
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shared Pagination, same as every other list in the app. */}
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={handlePageChange}
            totalItems={totalItems}
            limit={10}
          />
        </div>
      )}
    </SettingsPanel>
  );
}

export default Billing;
